import React, { useEffect, useState } from "react";
import RecipeCard from "./RecipeCard";
import { supabase } from "../supabaseClient";
import { RecipeCardSkeleton } from "./LoadingSkeleton";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import "../styles/home.css";
import logo from "../assets/images/logo.jpg";

export default function Home() {
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);

  const [activeType, setActiveType] = useState(null); // "slatko", "slano", or null
  const [loading, setLoading] = useState(true);

  // ---------------------------------------------------
  // LOAD RECIPES + CATEGORY TYPES
  // ---------------------------------------------------
  useEffect(() => {
    async function load() {
      setLoading(true);

      // Recepti
      const { data: recipesData, error: recErr } = await supabase
        .from("recipes")
        .select("*, recipe_images(image_url)")
        .order("created_at", { ascending: false });

      // Kategorije (ključne za slatko/slano)
      const { data: catData, error: catErr } = await supabase
        .from("categories")
        .select("*");

      if (recErr || catErr) {
        console.error("Greška kod Supabase:", recErr || catErr);
        setLoading(false);
        return;
      }

      setRecipes(recipesData || []);
      setCategories(catData || []);
      setLoading(false);
    }

    load();
  }, []);

  // ---------------------------------------------------
  // FILTERS USING CATEGORY TYPE
  // ---------------------------------------------------

  const findType = (recipe) => {
    const cat = categories.find((c) => c.slug === recipe.category);
    return cat?.type; // "slatko" ili "slano"
  };

  const featured = recipes.filter((r) => r.highlight === true);
  
  // Filtriranje samo ako su kategorije učitane
  const slatko = categories.length > 0 
    ? recipes.filter((r) => findType(r) === "slatko")
    : [];
  
  const slano = categories.length > 0 
    ? recipes.filter((r) => findType(r) === "slano")
    : [];

  // Grupiranje recepta po kategorijama
  const groupByCategory = (recipeList, type) => {
    const grouped = {};
    recipeList.forEach((recipe) => {
      let catSlug = recipe.category;
      
      // Za slano: prebaci recepte iz "pizza" kategorije u "ostalo"
      if (type === "slano" && (catSlug === "pizza" || catSlug?.toLowerCase().includes("pizza"))) {
        catSlug = "ostalo";
      }
      
      if (!grouped[catSlug]) {
        grouped[catSlug] = [];
      }
      grouped[catSlug].push(recipe);
    });
    return grouped;
  };

  const slatkoByCategory = groupByCategory(slatko, "slatko");
  const slanoByCategory = groupByCategory(slano, "slano");

  // Funkcija za sortiranje kategorija
  const sortCategories = (categorySlugs, type) => {
    const sorted = [...categorySlugs];
    
    if (type === "slano") {
      // Izbaci "pizza" kategoriju
      const filtered = sorted.filter(slug => {
        const slugLower = slug?.toLowerCase() || "";
        return !slugLower.includes("pizza");
      });
      
      // Sortiraj: "juhe" iznad "ostalo", "ostalo" na dno
      filtered.sort((a, b) => {
        const aName = (a?.toLowerCase() || "");
        const bName = (b?.toLowerCase() || "");
        
        const aIsOstalo = aName.includes("ostalo");
        const bIsOstalo = bName.includes("ostalo");
        const aIsJuhe = aName.includes("juhe");
        const bIsJuhe = bName.includes("juhe");
        
        // Odredi prioritet: ostalo=3, juhe=2, ostale=1
        const aPriority = aIsOstalo ? 3 : (aIsJuhe ? 2 : 1);
        const bPriority = bIsOstalo ? 3 : (bIsJuhe ? 2 : 1);
        
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        
        // Ako su istog prioriteta, sortiraj abecedno
        const aCat = categories.find(c => c.slug === a);
        const bCat = categories.find(c => c.slug === b);
        return (aCat?.name || a).localeCompare(bCat?.name || b);
      });
      
      return filtered;
    } else if (type === "slatko") {
      // Sortiraj: "ostalo" na dno
      sorted.sort((a, b) => {
        const aName = (a?.toLowerCase() || "");
        const bName = (b?.toLowerCase() || "");
        
        const aIsOstalo = aName.includes("ostalo");
        const bIsOstalo = bName.includes("ostalo");
        
        if (aIsOstalo && !bIsOstalo) return 1; // a ide na dno
        if (!aIsOstalo && bIsOstalo) return -1; // b ide na dno
        
        // Ostale sortiraj abecedno
        const aCat = categories.find(c => c.slug === a);
        const bCat = categories.find(c => c.slug === b);
        return (aCat?.name || a).localeCompare(bCat?.name || b);
      });
      
      return sorted;
    }
    
    return sorted;
  };

  // Dobivanje imena kategorije po slug-u
  const getCategoryName = (slug) => {
    const cat = categories.find((c) => c.slug === slug);
    return cat?.name || slug;
  };

  // ---------------------------------------------------
  // RENDER
  // ---------------------------------------------------
  return (
    <div className="home">

      {/* LOGO */}
      <div className="home-logo-wrap">
        <img src={logo} alt="Slatko bez glutena logo" className="logo" />
      </div>

      {/* HERO */}
      <header className="hero">
        <h1>Dobrodošli — Slatko bez glutena</h1>
        <p>Bezglutenski recepti s okusom doma.</p>
      </header>

      {/* FEATURED */}
      <section className="featured-slider">
        <h2>Istaknuti recepti</h2>

        {loading ? (
          <div className="grid">
            {[...Array(3)].map((_, i) => (
              <RecipeCardSkeleton key={i} />
            ))}
          </div>
        ) : featured.length > 0 ? (
          <Swiper
            modules={[Autoplay, Pagination, Navigation]}
            spaceBetween={30}
            slidesPerView={3}
            pagination={{ clickable: true }}
            navigation={true}
            autoplay={{ delay: 3500, disableOnInteraction: false }}
            loop={featured.length >= 3}
            breakpoints={{
              0: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
          >
            {featured.map((r) => (
              <SwiperSlide key={r.id}>
                <RecipeCard recipe={r} />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <p className="no-recipes">Trenutno nema istaknutih recepata.</p>
        )}
      </section>

      {/* CATEGORY BUTTONS */}
      <section className="category-buttons">
        <button 
          className={`btn-accent ${activeType === "slatko" ? "active" : ""}`}
          onClick={() => setActiveType(activeType === "slatko" ? null : "slatko")}
        >
          🍰 Slatki recepti
        </button>

        <button 
          className={`btn-accent-outline ${activeType === "slano" ? "active" : ""}`}
          onClick={() => setActiveType(activeType === "slano" ? null : "slano")}
        >
          🥖 Slani recepti
        </button>
      </section>

      {/* SLATKO */}
      {activeType === "slatko" && (
        <section className="recipe-section">
          <h2>Slatki recepti 🍰</h2>

          {loading ? (
            <div className="grid">
              {[...Array(6)].map((_, i) => (
                <RecipeCardSkeleton key={i} />
              ))}
            </div>
          ) : Object.keys(slatkoByCategory).length > 0 ? (
            sortCategories(Object.keys(slatkoByCategory), "slatko").map((catSlug) => (
              <div key={catSlug} style={{ marginBottom: "40px" }}>
                <h3 style={{ 
                  fontSize: "1.5rem", 
                  marginBottom: "20px", 
                  color: "#1f6074",
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 600
                }}>
                  {getCategoryName(catSlug)}
                </h3>
                <div className="grid">
                  {slatkoByCategory[catSlug].map((r) => (
                    <RecipeCard key={r.id} recipe={r} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <p className="no-recipes">Trenutno nema slatkih recepata.</p>
          )}
        </section>
      )}

      {/* SLANO */}
      {activeType === "slano" && (
        <section className="recipe-section">
          <h2>Slani recepti 🥖</h2>

          {loading ? (
            <div className="grid">
              {[...Array(6)].map((_, i) => (
                <RecipeCardSkeleton key={i} />
              ))}
            </div>
          ) : Object.keys(slanoByCategory).length > 0 ? (
            sortCategories(Object.keys(slanoByCategory), "slano").map((catSlug) => (
              <div key={catSlug} style={{ marginBottom: "40px" }}>
                <h3 style={{ 
                  fontSize: "1.5rem", 
                  marginBottom: "20px", 
                  color: "#1f6074",
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 600
                }}>
                  {getCategoryName(catSlug)}
                </h3>
                <div className="grid">
                  {slanoByCategory[catSlug].map((r) => (
                    <RecipeCard key={r.id} recipe={r} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <p className="no-recipes">Trenutno nema slanih recepata.</p>
          )}
        </section>
      )}
    </div>
  );
}
